import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import axios from '../../services/axios';

const NotificationBell = () => {
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pulse, setPulse] = useState(false);
    const prevUnread = useRef(0);
    const panelRef = useRef(null);

    const loadUnread = useCallback(async () => {
        try {
            const res = await axios.get('/student/notifications', {
                params: { unreadOnly: true, limit: 8 }
            });
            const next = res.data.unread ?? (res.data.data || []).length;
            if (next > prevUnread.current) setPulse(true);
            prevUnread.current = next;
            setUnread(next);
            if (open) setItems(res.data.data || []);
        } catch (_) {
            /* silent — bell is non-critical */
        }
    }, [open]);

    useEffect(() => {
        loadUnread();
        const id = setInterval(loadUnread, 60000);
        return () => clearInterval(id);
    }, [loadUnread]);

    useEffect(() => {
        if (!pulse) return undefined;
        const t = setTimeout(() => setPulse(false), 800);
        return () => clearTimeout(t);
    }, [pulse]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next) {
            setLoading(true);
            try {
                const res = await axios.get('/student/notifications', {
                    params: { limit: 8 }
                });
                setItems(res.data.data || []);
                setUnread(res.data.unread ?? 0);
            } catch (_) {
                setItems([]);
            } finally {
                setLoading(false);
            }
        }
    };

    const markRead = async (id) => {
        try {
            await axios.put(`/student/notifications/${id}/read`);
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
            setUnread((u) => Math.max(0, u - 1));
        } catch (_) {
            /* ignore */
        }
    };

    return (
        <div className="relative" ref={panelRef}>
            <button
                type="button"
                onClick={toggle}
                className={`btn-icon relative ${pulse ? 'animate-fade-in' : ''}`}
                aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
                aria-expanded={open}
                aria-haspopup="true"
            >
                <BellIcon className="h-5 w-5" aria-hidden="true" />
                {unread > 0 && (
                    <span className="absolute top-1.5 right-1.5 min-w-[1rem] h-4 px-1 rounded-full bg-brand-700 text-white text-[10px] leading-4 text-center font-semibold">
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-border rounded-xl shadow-soft z-30 animate-fade-up overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-ink-50/80">
                        <span className="text-sm font-semibold text-ink-950">Notifications</span>
                        <Link
                            to="/notifications"
                            className="text-xs font-semibold text-brand-700 hover:text-brand-800"
                            onClick={() => setOpen(false)}
                        >
                            View all
                        </Link>
                    </div>
                    <ul className="max-h-80 overflow-y-auto divide-y divide-border">
                        {loading && (
                            <li className="px-4 py-5">
                                <div className="skeleton h-4 w-2/3 mb-2" />
                                <div className="skeleton h-3 w-full" />
                            </li>
                        )}
                        {!loading && items.length === 0 && (
                            <li className="px-4 py-6 text-center text-sm text-ink-500">You&apos;re all caught up.</li>
                        )}
                        {!loading &&
                            items.map((n) => (
                                <li key={n.id} className={!n.is_read ? 'bg-brand-50/50' : ''}>
                                    {n.link_url ? (
                                        <Link
                                            to={n.link_url}
                                            className="block w-full text-left px-4 py-3 hover:bg-ink-50 transition-colors"
                                            onClick={() => {
                                                if (!n.is_read) markRead(n.id);
                                                setOpen(false);
                                            }}
                                        >
                                            <p className="text-sm font-semibold text-ink-950">{n.title}</p>
                                            <p className="text-xs text-ink-600 mt-0.5 line-clamp-2">{n.body}</p>
                                        </Link>
                                    ) : (
                                        <button
                                            type="button"
                                            className="w-full text-left px-4 py-3 hover:bg-ink-50 transition-colors"
                                            onClick={() => {
                                                if (!n.is_read) markRead(n.id);
                                            }}
                                        >
                                            <p className="text-sm font-semibold text-ink-950">{n.title}</p>
                                            <p className="text-xs text-ink-600 mt-0.5 line-clamp-2">{n.body}</p>
                                        </button>
                                    )}
                                </li>
                            ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
